import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import Form from '@shared/atoms/Form';
import FormAutocompleteWithList from '@shared/atoms/FormFields/FormAutocompleteWithList';
import FormImage from '@shared/atoms/FormFields/FormImage';
import Tooltip from '@shared/atoms/Tooltip';
import T from '@shared/atoms/Typography';

import { newSelectTranslation, useForm } from '@shared/utils';
import theme from '@shared/themes/default';

import {
  AUTOCOMPLETEWITHLIST_TYPE,
  courseStatus,
  DOMAIN_LOCALES,
  PROCESSING_STATUS,
} from '@shared/const';

import { useAppGlobals } from '../../../../contexts/AppContext';
import { TABS, useCourse } from '../../../../contexts/CourseContext';
import { hasAllEpisodesWithoutSubtitles } from '../../utils/episodeValidation';
import { getStaticImagesHostname } from '../../../../../config';

import ValidationList from '../../components/ValidationList';
import { listCategories, listExperts } from './graphql/queries';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  padding: ${theme.spacing.s};
`;

const HeaderStyled = styled.header`
  display: flex;
  justify-content: flex-end;
  padding: ${theme.spacing.s};
`;

const CatalogWrapper = styled.section`
  display: flex;
  flex-direction: column;
  max-width: 418px;
  row-gap: ${theme.spacing.m2};
`;

const Display = () => {
  const { t } = useTranslation();
  const { domain, getRestClient, useNewGraphQLQuery, videocationCatalog } =
    useAppGlobals();
  const languages = DOMAIN_LOCALES[domain];

  const {
    course,
    hasChanged,
    loading,
    locked,
    saveDisplay,
    setHandleSave,
    setHasChanged,
    setUploadingFiles,
    submitting,
    validateCourse,
  } = useCourse();

  const [tabValidation, setTabValidation] = useState({});

  const listCategoriesResponse = useNewGraphQLQuery(listCategories, {
    variables: {
      pagination: { limit: 1000 },
    },
  });
  const listExpertsResponse = useNewGraphQLQuery(listExperts, {
    variables: {
      pagination: { limit: 1000 },
    },
  });

  const categories = listCategoriesResponse.data?.listCategories || [];
  const experts = listExpertsResponse.data?.listExperts || [];

  const isPublished = course?.status === courseStatus.PUBLISHED;

  const isPrivateCourse = !course.catalogs?.some(
    (catalog) => catalog.id === videocationCatalog?.id
  );

  const {
    disableSubmit,
    edited,
    handleChange,
    handleSubmit,
    properties,
    setDefaults,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      categories: {},
      experts: {
        required: isPublished && !isPrivateCourse,
      },
      thumbnail: {
        required: isPublished,
      },
      promoPicture: {},
    },
    onSubmit: saveDisplay,
  });

  useEffect(() => {
    setDefaults({
      categories: course?.categories?.length ? course.categories : null,
      experts: course?.experts?.length ? course.experts : null,
      thumbnail: course?.thumbnail,
      promoPicture: course?.promoPicture,
    });
  }, [course]);

  useEffect(() => {
    if (course?.status === courseStatus.PUBLISHED) {
      // Merge current form values with original course data for proper validation
      const courseForValidation = {
        ...course,
        ...values,
      };

      const originalWithoutSubtitles = hasAllEpisodesWithoutSubtitles(
        course?.episodes
      );

      const validationStatus = validateCourse(
        courseForValidation,
        languages,
        originalWithoutSubtitles,
        videocationCatalog,
        course
      );
      // Check the catalog validation and include the images that are ready for processing
      let processedCatalog = {};
      if (validationStatus[TABS.CATALOG]) {
        const { promo, thumbnail, ...status } = validationStatus[TABS.CATALOG];
        delete validationStatus[TABS.CATALOG];
        processedCatalog = { ...status };

        // Only add promo validation if it was originally required AND file is missing
        if (
          promo &&
          !values.promoPicture?.file?.processingStatus &&
          !values.promoPicture?.file?.key
        ) {
          processedCatalog.promo = true;
        }

        // Only add thumbnail validation if it was originally required AND file is missing
        if (
          thumbnail &&
          !values.thumbnail?.file?.processingStatus &&
          !values.thumbnail?.file?.key
        ) {
          processedCatalog.thumbnail = true;
        }
      }
      setTabValidation({
        ...validationStatus,
        ...(Object.keys(processedCatalog).length
          ? { [TABS.CATALOG]: processedCatalog }
          : {}),
      });
    }
  }, [values, loading, videocationCatalog]);

  useEffect(() => {
    setHandleSave(() => handleSubmit);
  }, [values, disableSubmit]);

  useEffect(() => {
    setHasChanged(edited);
  }, [edited]);

  const handleAddCategory = (categoryId) => {
    const { id, name } = categories.find(
      (category) => category.id === categoryId
    );
    const newCourseCategories = [...(values.categories || []), { id, name }];

    handleChange({ name: 'categories', value: newCourseCategories });
  };
  const handleAddExpert = (expertId) => {
    const { id, firstName, lastName } = experts.find(
      (expert) => expert.id === expertId
    );
    const newCourseExperts = [
      ...(values.experts || []),
      { id, firstName, lastName },
    ];

    handleChange({ name: 'experts', value: newCourseExperts });
  };
  const handleDeleteCategory = (categoryId) => {
    const categoryIndex = values.categories.findIndex(
      (category) => category.id === categoryId
    );
    let newCourseCategories = [
      ...values.categories.slice(0, categoryIndex),
      ...values.categories.slice(categoryIndex + 1),
    ];

    if (!newCourseCategories.length) {
      newCourseCategories = null;
    }

    handleChange({ name: 'categories', value: newCourseCategories });
  };
  const handleDeleteExpert = (expertId) => {
    const expertIndex = values.experts.findIndex(
      (expert) => expert.id === expertId
    );
    let newCourseExperts = [
      ...values.experts.slice(0, expertIndex),
      ...values.experts.slice(expertIndex + 1),
    ];

    if (!newCourseExperts.length) {
      newCourseExperts = null;
    }

    handleChange({ name: 'experts', value: newCourseExperts });
  };
  const handleAddImage = (name, file) => {
    handleChange({
      name,
      value: {
        file: {
          key: file.s3File,
          processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
        },
        base64: file.base64,
      },
    });
  };
  const handleDeleteImage = (name) => {
    handleChange({
      name,
      value: null,
    });
  };

  const onStartUpload = () => {
    if (!hasChanged) setHasChanged(true);
    setUploadingFiles(true);
  };

  const onCompleteImageUpload = (fieldName) => () => {
    setUploadingFiles(false);
    // Clear the processing status to remove the overlay
    if (values[fieldName]?.file) {
      handleChange({
        name: fieldName,
        value: {
          ...values[fieldName],
          file: {
            ...values[fieldName].file,
            processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
          },
        },
      });
    }
  };

  const saveDisabled =
    disableSubmit ||
    (isPublished && locked) ||
    (isPublished && Object.keys(tabValidation[TABS.CATALOG] || {}).length > 0);

  return (
    <SectionStyled>
      <Form onSubmit={handleSubmit}>
        <HeaderStyled>
          <Tooltip
            arrow
            title={
              course.status === courseStatus.PUBLISHED &&
              saveDisabled &&
              !locked &&
              !submitting &&
              Object.keys(tabValidation[TABS.CATALOG] || {}).length > 0 ? (
                <>
                  <T color={theme.palette.mainDark}>
                    {t('backoffice.courses.publishing.save')}
                  </T>
                  <ValidationList details={tabValidation} />
                </>
              ) : (
                ''
              )
            }
          >
            <div>
              <Button
                ariaLabel={t('administrate.saveInformation')}
                disabled={saveDisabled}
                loading={submitting}
                submit
                theme="dark"
                type="secondary"
              >
                {t('generic.save')}
              </Button>
            </div>
          </Tooltip>
        </HeaderStyled>
        <CatalogWrapper>
          <CatalogWrapper>
            <FormAutocompleteWithList
              disabled={
                (isPublished && locked) ||
                submitting ||
                listCategoriesResponse.loading ||
                listCategoriesResponse.error
              }
              filterOptions
              id="categories"
              name="categories"
              items={categories.filter(Boolean).map((category) => ({
                key: category.id,
                title: newSelectTranslation(category.name),
              }))}
              label={t('backoffice.courses.displayTab.categories')}
              loading={listCategoriesResponse.loading}
              loadingText={t('backoffice.courses.displayTab.categoriesLoading')}
              noOptionsText={t('backoffice.courses.displayTab.categoriesEmpty')}
              onAdd={handleAddCategory}
              onDelete={handleDeleteCategory}
              placeholder={t('backoffice.courses.displayTab.search')}
              properties={properties}
              selectedItems={(values.categories || [])
                .filter(Boolean)
                .map((category) => ({
                  key: category.id,
                  title: newSelectTranslation(category.name),
                }))}
              theme="dark"
              type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
            />
            <FormAutocompleteWithList
              disabled={
                (isPublished && locked) ||
                submitting ||
                listExpertsResponse.loading ||
                listExpertsResponse.error
              }
              filterOptions
              id="experts"
              infoText={t('backoffice.courses.displayTab.expertsInfo')}
              items={experts.map((expert) => ({
                key: expert.id,
                title: `${expert.firstName} ${expert.lastName}`,
              }))}
              label={t('backoffice.courses.displayTab.experts')}
              limit={2}
              loading={listExpertsResponse.loading}
              loadingText={t('backoffice.courses.displayTab.expertsLoading')}
              name="experts"
              noOptionsText={t('backoffice.courses.displayTab.expertsEmpty')}
              onAdd={handleAddExpert}
              onDelete={handleDeleteExpert}
              selectedItems={(values.experts || []).map((expert) => ({
                key: expert.id,
                title: `${expert.firstName} ${expert.lastName}`,
              }))}
              placeholder={t('backoffice.courses.displayTab.search')}
              properties={properties}
              theme="dark"
              type={AUTOCOMPLETEWITHLIST_TYPE.FORM}
            />
            <FormImage
              descriptionText={[t('backoffice.courses.displayTab.image')]}
              disabled={(isPublished && locked) || submitting}
              handleAdd={(file) => handleAddImage('thumbnail', file)}
              handleDelete={() => handleDeleteImage('thumbnail')}
              id="thumbnail"
              imgUrl={
                values.thumbnail?.file?.key &&
                (values.thumbnail.base64 ||
                  `${getStaticImagesHostname()}/${values.thumbnail.file.key}`)
              }
              infoText={
                isPublished && course.thumbnail
                  ? t('backoffice.courses.displayTab.thumbnailCannotDelete')
                  : t('backoffice.courses.displayTab.thumbnailInfo')
              }
              name="thumbnail"
              onCompleteUpload={onCompleteImageUpload('thumbnail')}
              onStartUpload={onStartUpload}
              processingStatus={values.thumbnail?.file?.processingStatus}
              properties={properties}
              restClient={getRestClient()}
              title={t('backoffice.courses.displayTab.thumbnail')}
              required
            />
            <FormImage
              descriptionText={[t('backoffice.courses.displayTab.image')]}
              disabled={(isPublished && locked) || submitting}
              handleAdd={(file) => handleAddImage('promoPicture', file)}
              handleDelete={() => handleDeleteImage('promoPicture')}
              id="promoPicture"
              imgUrl={
                values.promoPicture?.file?.key &&
                (values.promoPicture.base64 ||
                  `${getStaticImagesHostname()}/${values.promoPicture.file.key}`)
              }
              name="promoPicture"
              onCompleteUpload={onCompleteImageUpload('promoPicture')}
              onStartUpload={onStartUpload}
              infoText={
                isPublished && course.promoPicture
                  ? t('backoffice.courses.displayTab.promoCannotDelete')
                  : t('backoffice.courses.displayTab.promoInfo')
              }
              processingStatus={values.promoPicture?.file?.processingStatus}
              properties={properties}
              restClient={getRestClient()}
              title={t('backoffice.courses.displayTab.promo')}
            />
          </CatalogWrapper>
        </CatalogWrapper>
      </Form>
    </SectionStyled>
  );
};

export default Display;
